import { useEffect, useState } from "react";
import { Eye, Users, MessageCircle, Images, User, Award, Building2, ArrowRight, BookOpen } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { adminApi } from "@/lib/api";
import { useAdmin } from "../AdminContext";
import { PageHead, Panel } from "../fields";

const useStats = () => {
  const [s, setS] = useState(null);
  useEffect(() => { adminApi.get("/admin/stats").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  return s;
};

const StatCards = ({ s }) => {
  const cards = [
    { icon: Eye, tone: "bg-sky-100 text-sky-700", n: s?.views_today, label: "Page views today", id: "views-today" },
    { icon: Users, tone: "bg-purple-100 text-purple-600", n: s?.views_total, label: "Total page views", id: "views-total" },
    { icon: MessageCircle, tone: "bg-green-100 text-green-700", n: s?.whatsapp_total, label: "WhatsApp clicks (total)", id: "whatsapp-total" },
    { icon: Images, tone: "bg-pink-100 text-pink-600", n: s?.photos_uploaded, label: "Photos uploaded", id: "photos" },
  ];
  return (
    <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
      {cards.map((c) => (
        <div key={c.id} className="rounded-[1.75rem] bg-white p-5 shadow-sm ring-1 ring-slate-100" data-testid={`stat-${c.id}`}>
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${c.tone}`}><c.icon className="h-5 w-5" /></span>
          <p className="font-display mt-3 text-3xl font-semibold text-slate-900">{c.n ?? "–"}</p>
          <p className="text-sm text-slate-500">{c.label}</p>
        </div>
      ))}
    </div>
  );
};

export const Dashboard = ({ go }) => {
  const { val } = useAdmin();
  const s = useStats();
  const actions = [
    { icon: User, label: "Edit doctor profile & photo", to: "profile" },
    { icon: Images, label: "Upload gallery photos", to: "gallery" },
    { icon: Award, label: "Add an award or certificate", to: "awards" },
    { icon: Building2, label: "Change address or timings", to: "clinic" },
    { icon: BookOpen, label: "Write a health article", to: "library" },
    { icon: MessageCircle, label: "Change WhatsApp number", to: "whatsapp" },
  ];
  return (
    <div>
      <PageHead title={`Welcome, ${val("profile.name")}`} desc="Update your website here. Edit any page, click Save Changes, then Publish Changes to make it live." />
      <StatCards s={s} />
      <Panel title="Quick actions">
        <div className="grid gap-3 sm:grid-cols-2">
          {actions.map((a) => (
            <button key={a.to} onClick={() => go(a.to)} data-testid={`quick-${a.to}`} className="group flex items-center gap-3 rounded-2xl bg-sky-50/70 px-4 py-3.5 text-left text-[15px] font-medium text-blue-900 transition-colors hover:bg-sky-100">
              <a.icon className="h-5 w-5" /> <span className="flex-1">{a.label}</span> <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          ))}
        </div>
      </Panel>
      <Panel title="How updating works">
        <ol className="list-decimal space-y-2 pl-5 text-[15px] text-slate-700">
          <li>Open any page from the menu and make your changes.</li>
          <li>Click <b>Save Changes</b> to keep them as a draft (visitors don't see drafts).</li>
          <li>Click <b>Preview</b> to see how the website will look.</li>
          <li>Click <b>Publish Changes</b> to make them live for everyone.</li>
        </ol>
      </Panel>
    </div>
  );
};

export const Analytics = () => {
  const s = useStats();
  return (
    <div>
      <PageHead title="Website Analytics" desc="How many people visit your website and tap the WhatsApp button." />
      <StatCards s={s} />
      <Panel title="Last 14 days">
        <div className="h-72" data-testid="analytics-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={s?.last_14_days || []}>
              <XAxis dataKey="date" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Legend />
              <Bar dataKey="views" name="Page views" fill="#7dd3fc" radius={[6, 6, 0, 0]} />
              <Bar dataKey="whatsapp" name="WhatsApp clicks" fill="#25D366" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
};
